<template>
  <div
    :class="[
      'UIInputTextarea',
      {
        'UIInputTextarea--no-margin': noMargin,
        // 'UIInputTextarea--has-errors': hasErrors,
        'UIInputTextarea--is-disabled': isDisabled,
      },
    ]"
  >
    <UIInputLabel
      v-if="label || $slots['label']"
      class="UIInputTextarea__label"
      :for="name"
      :isRequired="isRequired"
      :isOptional="isOptional"
    >
      <slot name="label">{{ label }}</slot>
      <template #after-label>
        <slot name="after-label" />
      </template>

      <slot name="tooltip">
        <UITooltip v-if="$slots['tooltip-content']">
          <template #content>
            <slot name="tooltip-content" />
          </template>
        </UITooltip>
      </slot>
    </UIInputLabel>
    <UIInputValidationWrapper :errors="errors" :uiLib="uiLib">
      <textarea
        :id="name"
        :data-cy="`input[${name}]`"
        :data-testid="`input[${name}]`"
        :rows="rows"
        class="UIInputTextarea__input"
        v-bind="$attrs"
        v-model="value"
        :maxLength="maxLength"
        :disabled="isDisabled"
        :readonly="isReadonly"
      />
      <div
        class="UIInputTextarea__character-counter"
        v-if="showCharacterCounter"
      >
        {{ String(value || '').length }}/{{ maxLength }}
      </div>
    </UIInputValidationWrapper>
  </div>
</template>

<script lang="ts" setup>
import { useField } from 'vee-validate'
import { toRef } from 'vue'
import UIInputLabel from './UIInputLabel.vue'
import UITooltip from '../tooltips/UITooltip.vue'
import { BaseSchema, mixed } from 'yup'
import UIInputValidationWrapper from './UIInputValidationWrapper.vue'

defineOptions({
  inheritAttrs: false,
})

const props = withDefaults(
  defineProps<{
    label?: string
    name: string
    modelValue?: string
    isDisabled?: boolean
    isReadonly?: boolean
    isRequired?: boolean
    isOptional?: boolean
    rows?: number
    noMargin?: boolean
    maxLength?: number
    showCharacterCounter?: boolean
    validator?: BaseSchema
    uiLib?: 'dashlite' | 'flow'
  }>(),
  {
    isDisabled: false,
    isReadonly: false,
    isRequired: false,
    rows: 5,
    noMargin: true,
    maxLength: 65536,
    validator: () => mixed(),
    uiLib: 'dashlite',
  },
)
// const hasErrors = computed(() => errors.value && errors.value.length)
const { value, errors } = useField(
  toRef(props, 'name'),
  toRef(props, 'validator'),
  { initialValue: props.modelValue },
)
</script>

<style scoped lang="scss">
.UIInputTextarea {
  display: flex;
  flex-direction: column;
  position: relative;

  --active-color: #{$color-ui-accent-main};
  --label-color: #{$color-ui-gray-cool-700};
  --focus-shadow: 0 0 0 3px rgba(15, 172, 129, 0.1);

  &.UIInputTextarea--no-margin {
    margin-bottom: 0;
  }

  &.UIInputTextarea--has-errors {
    --active-color: #{$newcolor-danger-main};
    --label-color: #{$newcolor-danger-main};
    --focus-shadow: 0 0 0 3px rgba(232, 83, 71, 0.1);

    .UIInputTextarea__input {
      border-color: var(--active-color) !important;
    }
  }

  &.UIInputTextarea--is-disabled {
    pointer-events: none;

    .UIInputTextarea__input {
      background-color: $newcolor-grey-100;
    }
  }

  .UIInputTextarea__character-counter {
    font-size: 0.75rem;
    color: $color-ui-gray-cool-500;
    position: absolute;
    bottom: 0.5rem;
    right: 0.5rem;
    font-style: italic;
    font-weight: 400;
  }

  .UIInputTextarea__input {
    @include bodyText();
    display: block;
    width: 100%;
    padding: 0.4375rem 1rem;
    color: $newcolor-grey-700;
    background-color: $color-white;
    border: 1px solid $newcolor-border;
    border-radius: 8px;
    transition:
      border-color 0.15s ease-in-out,
      box-shadow 0.15s ease-in-out;
    width: 100%;

    &:focus {
      border-color: var(--active-color);
      outline: 0;
      box-shadow: var(--focus-shadow);
    }

    &::placeholder {
      @include font-size-16px();
      color: $color-ui-gray-cool-500;
    }
  }

  // .UIInputTextarea__error {
  //   font-size: 0.75rem;
  //   line-height: 1.25rem;
  //   color: $newcolor-danger-main;
  //   position: absolute;
  //   bottom: -1.25rem;
  // }
}
</style>
